import React, { useMemo, useState } from "react";
import { Alert } from "antd";
import { Modal } from "../Modal";
import { Select } from "../Select";
import { Input } from "../Input";
import { Button } from "../Button";
import { Table } from "../Table";
import { Skeleton } from "../Skeleton";
import { Div, Text } from "../../styles/Common";
import { theme } from "../../styles/theme";
import searchIcon from "../../assets/icons/search.svg";
import useDebounce from "../../core/hooks/useDebounce";
import useGetDocumentType from "../../pages/Documents/hooks/useGetDocumentType";
import useExportDocuments from "../../pages/Companies/hooks/useExportDocuments";
import { toast } from "react-hot-toast";

export const ModalExportDocuments = ({
  openModal,
  handleClose,
  companiesOptions,
}) => {
  const exportDocuments = useExportDocuments();
  const { isLoading: loadingExport } = exportDocuments;

  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [documentTypeName, setDocumentTypeName] = useState("");
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const searchValue = useDebounce(documentTypeName, 600);

  const getDocuments = useGetDocumentType(searchValue);
  const {
    data: documentsData,
    isLoading: loadingDocuments,
    refetch: refetchDocumentTypes,
  } = getDocuments;
  const documentList = useMemo(
    () =>
      (documentsData?.data || []).map((ele) => ({
        ...ele,
        key: `${ele.id}`,
      })),
    [documentsData]
  );

  const handleSelectCompany = (value) => {
    setSelectedCompanyId(value);
    setSelectedRowKeys([]);
    setErrorMessage("");
    refetchDocumentTypes();
  };

  const handleChangeSearch = (event) => {
    const { value } = event.target;
    setDocumentTypeName(value);
    setTablePagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleChangeTable = (pagination) => {
    setTablePagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleReset = () => {
    setSelectedCompanyId(null);
    setSelectedRowKeys([]);
    setErrorMessage("");
    setDocumentTypeName("");
    setTablePagination({ current: 1, pageSize: 10 });
    handleClose();
  };

  const handleExport = () => {
    if (selectedRowKeys.length === 0) {
      setErrorMessage("Please select at least one document to export.");
      return;
    }

    setErrorMessage("");
    const documentTypeIds = selectedRowKeys.map((key) => Number(key));

    exportDocuments.reset();
    exportDocuments.mutate(
      { companyId: selectedCompanyId, documentTypeIds },
      {
        onSuccess: (response) => {
          toast.success(
            response?.data?.message ||
              "Processing your request. A download link for the ZIP archive will be sent to your email shortly.",
            { duration: 8000 }
          );
          handleReset();
        },
        onError: (err) => {
          setErrorMessage(
            err?.response?.data?.Errors?.[0] ||
              "Error starting the export process."
          );
        },
      }
    );
  };

  const tableColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setErrorMessage("");
    },
    preserveSelectedRowKeys: true,
  };

  return (
    <Modal
      open={openModal}
      onCancel={handleReset}
      align={"left"}
      padding={"41px 39px"}
      width={"700px"}
    >
      <Modal.Header padding="16px 0 16px 0" title={"Export Documents"} />
      <Modal.Body margin="16px 0 24px 0">
        <Div direction="column" gap="16px">
          <Select
            options={companiesOptions}
            value={selectedCompanyId}
            onChange={handleSelectCompany}
            placeholder="Select a company"
            showSearch
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: "100%" }}
          />
          {selectedCompanyId && (
            <>
              <Input
                placeholder="Search document type..."
                suffix={<img alt="search" src={searchIcon} />}
                onChange={handleChangeSearch}
                value={documentTypeName}
              />
              <Table
                columns={tableColumns}
                data={documentList}
                rowSelection={rowSelection}
                small
                scroll={tablePagination.pageSize > 10 ? { y: 320 } : undefined}
                pagination={{
                  defaultCurrent: 1,
                  ...tablePagination,
                  total: documentList.length,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "50", "100"],
                  locale: {
                    items_per_page: "",
                  },
                  position: ["bottomCenter"],
                }}
                onChange={handleChangeTable}
                rowClassName={(record, index) =>
                  index % 2 === 0 ? "table-row-light" : "table-row-dark"
                }
                sizerLabels={["Show", "Results"]}
                locale={{
                  emptyText: loadingDocuments ? (
                    <Div justify="center" gap="16px">
                      {[...Array(3)].map((v, idx) => (
                        <Skeleton
                          title={false}
                          paragraph={{ rows: 5, width: "100%" }}
                          loading
                          key={idx}
                        />
                      ))}
                    </Div>
                  ) : (
                    "No documents found"
                  ),
                }}
              />
              {selectedRowKeys.length > 0 && (
                <Text size={theme.fonts.size.sm} color={theme.colors.grey}>
                  {selectedRowKeys.length} documents selected
                </Text>
              )}
            </>
          )}
        </Div>
      </Modal.Body>
      <Modal.Footer style={{ flexDirection: "column" }}>
        {errorMessage !== "" && (
          <Div m="0 0 20px 0" justify="flex-end">
            <Alert
              closable
              message={errorMessage}
              type="error"
              onClose={() => setErrorMessage("")}
            />
          </Div>
        )}
        <Div gap="32px" justify="center">
          <Button
            onClick={handleReset}
            border={theme.colors.dark}
            color={theme.colors.dark}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            background={theme.colors.salmon}
            loading={loadingExport}
            disabled={!selectedCompanyId || selectedRowKeys.length === 0}
          >
            Export Documents
          </Button>
        </Div>
      </Modal.Footer>
    </Modal>
  );
};
